/**
 * Blob을 File 인터페이스 호환 객체로 변환
 *
 * Android WebView에서 new File() 생성자가 문제를 일으킬 수 있으므로,
 * Blob을 File 인터페이스와 호환되도록 확장합니다.
 *
 * @param blob 원본 Blob
 * @param filename 파일명
 * @returns File 인터페이스 호환 객체
 */
export function convertBlobToFile (blob: Blob, filename: string): File {
  // File 생성자 시도 (최신 브라우저)
  try {
    // File 생성자가 있고 정상 동작하는 경우
    if (typeof File !== 'undefined') {
      const file = new File([blob], filename, { type: blob.type })
      // File 생성이 성공했는지 검증
      if (file.name === filename && file.size === blob.size) {
        return file
      }
    }
  } catch (error) {
    // File 생성자 실패 시 폴백으로 진행
    console.warn('[toFile] File constructor failed, using Blob extension:', error)
  }

  // 폴백: Blob을 File처럼 동작하도록 확장
  // IMPORTANT: Object.create가 아닌 직접 캐스팅 사용
  // Object.create(blob)은 URL.createObjectURL()과 호환되지 않을 수 있음
  // 대신 Blob을 직접 File로 캐스팅하고 추가 속성만 설정
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fileBlob = blob as any

  // File 인터페이스 필수 속성 추가
  fileBlob.name = filename
  fileBlob.lastModified = Date.now()
  fileBlob.lastModifiedDate = new Date()
  fileBlob.webkitRelativePath = ''

  return fileBlob as File
}

// Blob → DataURL 변환 (FileReader 사용)
export function convertBlobToDataUrl (blob: Blob): Promise<string> {
  // alert('[convertBlobToDataUrl] 시작\n' + blob.size + ' bytes\n' + blob.type)

  return new Promise<string>((resolve, reject) => {
    try {
      const reader = new FileReader()

      reader.onload = () => {
        if (reader.result) {
          const result = reader.result as string
          resolve(result)
        } else {
          console.error('[blob-utils] convertBlobToDataUrl: reader.result is null')
          reject(new Error('FileReader result is null'))
        }
      }

      reader.onerror = () => {
        const error = reader.error
        console.error('[blob-utils] convertBlobToDataUrl: FileReader 오류:', error)
        reject(error)
      }

      // alert('[convertBlobToDataUrl] readAsDataURL 호출 중...')
      reader.readAsDataURL(blob)
    } catch (error) {
      console.error('[blob-utils] convertBlobToDataUrl: 예외 발생:', error)
      reject(error)
    }
  })
}

/**
 * 입력을 Blob으로 정규화
 */
export async function convertImageDataToBlob (input: File | Blob | string): Promise<Blob> {
  // URL/DataURL인 경우
  if (typeof input === 'string') {
    // DataURL인 경우
    if (input.startsWith('data:')) {
      return convertDataUrlToBlob(input)
    }

    // HTTP URL인 경우
    const response = await fetch(input)
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`)
    }
    return await response.blob()
  }

  // 이미 Blob/File인 경우
  // Cross-realm Blob인지 확인하여 필요시 ensureRealmBlob 호출
  if (needsRealmConversion(input)) {
    return ensureRealmBlob(input)
  }
  return input as Blob
}

/**
 * DataURL을 Blob으로 변환
 */
export function convertDataUrlToBlob (dataUrl: string): Blob {
  const arr = dataUrl.split(',')
  const mimeMatch = arr[0].match(/:(.*?);/)
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg'
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }

  return new Blob([u8arr], { type: mime })
}

/**
 * Blob/File이 cross-realm 변환이 필요한지 판단
 *
 * Cross-realm 문제는 다음 경우에 발생:
 * - Capacitor 플러그인에서 생성된 File/Blob
 * - iframe에서 전달된 File/Blob
 * - WebView와 네이티브 간 경계를 넘은 객체
 *
 * @param blob 검사할 Blob 또는 File
 * @returns true면 ensureRealmBlob 호출 필요
 */
function needsRealmConversion (blob: Blob): boolean {
  try {
    // 1. 생성자 체크 - 다른 realm의 객체는 생성자가 다를 수 있음
    if (blob.constructor.name !== 'Blob' && blob.constructor.name !== 'File') {
      console.warn('[needsRealmConversion] 비표준 생성자 감지:', blob.constructor.name)
      return true
    }

    // 2. 프로토타입 체크 - window.Blob과 다른 프로토타입이면 cross-realm
    if (typeof window !== 'undefined' && window.Blob) {
      const isNativeBlob = blob instanceof window.Blob
      if (!isNativeBlob) {
        console.warn('[needsRealmConversion] Cross-realm Blob 감지 (프로토타입 불일치)')
        return true
      }
    }

    // 3. 필수 메서드 체크 - 메서드가 없거나 올바르게 바인딩되지 않았으면 문제
    if (typeof blob.arrayBuffer !== 'function' ||
        typeof blob.slice !== 'function' ||
        typeof blob.text !== 'function') {
      console.warn('[needsRealmConversion] Blob 메서드 누락')
      return true
    }

    // 4. Android WebView 특정 체크
    // WebView에서는 File 객체가 FileReader에서 읽히지 않는 경우가 있음
    if (typeof navigator !== 'undefined' &&
        /Android/i.test(navigator.userAgent) &&
        blob instanceof File) {
      // Capacitor나 Cordova 환경 감지
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const win = window as any
      if (win.Capacitor || win.cordova) {
        return true
      }
    }

    // 5. 속성 접근 테스트 - 일부 cross-realm 객체는 속성 접근 시 오류 발생
    try {
      // 속성 접근 가능한지만 확인 (값은 사용하지 않음)
      void blob.size
      void blob.type
    } catch (e) {
      console.warn('[needsRealmConversion] Blob 속성 접근 실패:', e)
      return true
    }

    // 모든 체크 통과 - 정상적인 현재 realm의 Blob
    return false
  } catch (error) {
    // 체크 중 오류 발생 시 안전하게 변환
    console.warn('[needsRealmConversion] 체크 중 오류 발생, 변환 수행:', error)
    return true
  }
}

// 현재 realm의 진짜 Blob으로 강제 변환 (cross-realm File/Blob 이슈 방지)
async function ensureRealmBlob (fileOrBlob: File | Blob): Promise<Blob> {
  try {
    // alert('[ensureRealmBlob] 변환 시작\n' +
    //   fileOrBlob.constructor.name + '\n' +
    //   fileOrBlob.size + ' bytes\n' +
    //   fileOrBlob.type)
    // File/Blob 모두 arrayBuffer를 지원 — 새 Blob으로 재래핑하면 브랜드 이슈 해소됨
    const buffer = await (fileOrBlob as Blob).arrayBuffer()
    const type = (fileOrBlob as Blob).type || 'application/octet-stream'
    // alert('[ensureRealmBlob] arrayBuffer 성공\n' + buffer.byteLength + ' bytes')
    return new Blob([buffer], { type })
  } catch (error) {
    // 혹시 arrayBuffer를 지원하지 않는 가짜 객체라면, 마지막 수단으로 FileReader 사용
    // const errMsg = error instanceof Error ? error.message : String(error)
    console.warn('[ensureRealmBlob] arrayBuffer 실패, FileReader로 폴백:', error)
    // alert('[ensureRealmBlob] arrayBuffer 실패!\nFileReader로 폴백\nError: ' + errMsg)
    return await new Promise<Blob>((resolve, reject) => {
      const fr = new FileReader()
      fr.onerror = () => {
        console.error('[ensureRealmBlob] FileReader 오류:', fr.error)
        // alert('[ensureRealmBlob] FileReader 오류!\n' + (fr.error ? fr.error.toString() : 'Unknown error'))
        reject(fr.error)
      }
      fr.onload = () => {
        try {
          const buf = fr.result as ArrayBuffer
          const inferredType = ((): string => {
            if (fileOrBlob && typeof (fileOrBlob as Blob).type === 'string') {
              return (fileOrBlob as Blob).type || 'application/octet-stream'
            }
            return 'application/octet-stream'
          })()
          // alert('[ensureRealmBlob] FileReader 성공\n' + buf.byteLength + ' bytes')
          resolve(new Blob([buf], { type: inferredType }))
        } catch (e) {
          // const eMsg = e instanceof Error ? e.message : String(e)
          console.error('[ensureRealmBlob] Blob 생성 실패:', e)
          // alert('[ensureRealmBlob] Blob 생성 실패!\n' + eMsg)
          reject(e)
        }
      }
      try {
        fr.readAsArrayBuffer(fileOrBlob as Blob)
      } catch (e) {
        // const eMsg = e instanceof Error ? e.message : String(e)
        console.error('[ensureRealmBlob] readAsArrayBuffer 호출 실패:', e)
        // alert('[ensureRealmBlob] readAsArrayBuffer 호출 실패!\n' + eMsg)
        reject(e)
      }
    })
  }
}
